import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";

import API_URL from "../../api/API";
import { useBusinessAuthContext } from "../commoncomponents/useBusinessAuthContext";

import WaiverContentSectionSidebar from "./components/content/WaiverContentSectionSidebar";
import WaiverContentSectionEditor from "./components/content/WaiverContentSectionEditor";

const WaiverContentEditor = () => {

    const { business } = useBusinessAuthContext();

    const {
        waiver,
        templates, refreshChangeSummary,setSaveHandler,setHasUnsavedChanges,
    } = useOutletContext();

    const waiverId = waiver?._id;

    const [sections, setSections] = useState([]);

    const [activeIndex, setActiveIndex] = useState(0);

    const [editorKey, setEditorKey] = useState(0);

    const [saving, setSaving] = useState(false);

    const [isDirty, setIsDirty] = useState(false);

    const [governingData, setGoverningData] = useState([]);

    const activeSection = sections[activeIndex];

    
    
    /*
    ------------------------------------------------------------------
    Save Draft
    ------------------------------------------------------------------
    */

    const saveDraft = useCallback(async () => { ////////NEW 6th July 2026
        
        if (saving) return false;

        setSaving(true);

        try {

            const res = await fetch(

                `${API_URL.BUSINESS_WAIVER_SMART_STEP_WAIVER_SECTIONS}/${waiverId}`,

                {

                    method: "PATCH",

                    headers: {

                        Authorization: `Bearer ${business.token}`,

                        "Content-Type": "application/json",

                        "x-session-id": business.sessionId,

                        "x-frontend-url": window.location.pathname,

                    },

                    body: JSON.stringify({
                        sections,
                    }),

                }

            );

            const data = await res.json();

            if (!data.success) {

                alert(data.message);

                return false;

            }
            await refreshChangeSummary(); // refreshWaiver() must not be used, because it reloads changes overwritten here.
            return true;

        }
        catch (err) {

            console.error(err);

            return false;

        }
        finally {

            setSaving(false);

        }

    }, [
        waiverId,
        sections,
        business,
        refreshChangeSummary,
        saving,
    ]); /* Otherwise it will will still recreate saveHandler every render unless saveDraft is stable. */
    

    /* Each tab registers its save function*/  ////////NEW 6th July 2026
    useEffect(() => {
        setSaveHandler(() => saveDraft);
        return () => setSaveHandler(null);
    }, []); /* STOP tracking saveDraft in dependencies - else infintie rendering loop in browser */ 
    
    useEffect(() => {
        setHasUnsavedChanges(isDirty);
    }, [isDirty]); /* setHasUnsavedChanges DO NOT include setter in deps (it’s stable anyway). */
   
    
    /*
    ------------------------------------------------------------------
    Load waiver sections
    ------------------------------------------------------------------
    */

    useEffect(() => {

        if (!waiver) return;

         if (isDirty || saving) return;

        const ordered =
            [...(templates?.adultFull?.sections || [])]
                .sort((a,b)=>a.order-b.order);

        const governingSection = {
            _id: "governing-laws",
            title: "Governing Laws",
            status: "locked",
            system: true,
        };

        const lastSection = ordered[ordered.length - 1];

        setSections([
            ...ordered.slice(0, -1),
            governingSection,
            lastSection,
        ]);

    }, [waiver]); //, isDirty, saving

    /*
    ------------------------------------------------------------------
    Governing Laws
    ------------------------------------------------------------------
    */

    useEffect(() => {

        if (!waiver?.locations?.length) return;

        const loadGoverningLaws = async () => {

            try {

                const locRes = await fetch(
                    API_URL.BUSINESS_LOCATIONS,
                    {
                        headers: {
                            Authorization: `Bearer ${business.token}`,
                        },
                    }
                );

                const locJson = await locRes.json();

                const waiverLocationIds =
                    waiver.locations.map(l =>
                        String(typeof l === "string" ? l : l._id)
                    );

                const locations =
                    locJson.locations.filter(loc =>
                        waiverLocationIds.includes(String(loc._id))
                    );

                const states = [
                    ...new Set(locations.map(l => l.state))
                ];

                const clauseRes = await fetch(
                    `${API_URL.GET_STATE_CLAUSES_FOR_WAIVER}/${waiverId}?states=${states.join(",")}`,
                    {
                        headers: {
                            Authorization: `Bearer ${business.token}`,
                            "x-session-id": business.sessionId,
                            "x-frontend-url": window.location.pathname,
                        },
                    }
                );

                const clauseJson = await clauseRes.json();

                const uiData = locations.map(loc => ({

                    locationName: loc.name,

                    state: loc.state,

                    clause:
                        clauseJson.data.find(
                            c =>
                                c.state.trim().toLowerCase() ===
                                loc.state.trim().toLowerCase()
                        )?.body ||

                        "<p>No governing law found for this state.</p>",

                }));

                setGoverningData(uiData);

            }
            catch (err) {

                console.error(err);

            }

        };

        loadGoverningLaws();

    }, [waiver, business, waiverId]);

    /*
    ------------------------------------------------------------------
    Refresh Editor
    ------------------------------------------------------------------
    */

    useEffect(() => {

        setEditorKey(prev => prev + 1);

    }, [activeIndex]);

    /*
    ------------------------------------------------------------------
    Update Section
    ------------------------------------------------------------------
    */

    const updateContent = value => {

        if (activeSection.system) return;

        const updated = [...sections];

        updated[activeIndex].content = value;

        setSections(updated);

        setIsDirty(true);
        //setHasUnsavedChanges(true); ////////NEW 6th July 2026 - commented because The second effect already keeps them synchronized.

    };

    /*
    ------------------------------------------------------------------
    Auto Save
    ------------------------------------------------------------------
    */

    useEffect(() => {

        if (!isDirty) return;

        const timer = setTimeout(() => {

            //const ok = await saveDraft();
            saveDraft();

            //if (ok) {
                setIsDirty(false);
                setHasUnsavedChanges(false); ////////NEW 6th July 2026
            //}

        }, 1500);

        return () => clearTimeout(timer);

    }, [sections, isDirty]);




        /*
    ------------------------------------------------------------------
    Change Section
    ------------------------------------------------------------------
    */

    const handleSectionChange = async (index) => {

        if (index === activeIndex) return;

        if (isDirty) {

            const ok = await saveDraft();

            if (ok) {
                setIsDirty(false);
                setHasUnsavedChanges(false); ////////NEW 6th July 2026
            }

        }

        setActiveIndex(index);

    };

    /*
    ------------------------------------------------------------------
    Placeholder Values
    ------------------------------------------------------------------
    */

    const businessName = waiver?.businessId?.businessName || "";
    const businessEmail = waiver?.businessId?.email || "";
    const county =  waiver?.locations?.[0]?.county || "";

    const phValues = {
        BUSINESS_NAME: businessName,
        BUSINESS_EMAIL: businessEmail,
        COUNTY: county,
        MINOR_FULL_NAME: "the minor",
        MINOR_FIRST_NAME: "the minor",
        PREVIOUS_SIGNED_DATE: "THE PREVIOUS SIGNED DATE",
    };
    

    /*
    ------------------------------------------------------------------
    Render
    ------------------------------------------------------------------
    */

    return (

        <div className="flex gap-6">

            <WaiverContentSectionSidebar

                sections={sections}

                activeIndex={activeIndex}

                onSectionChange={handleSectionChange}

            />

            <WaiverContentSectionEditor

                activeSection={activeSection}

                governingData={governingData}

                saving={saving}

                editorKey={editorKey}

                phValues={phValues}

                updateContent={updateContent}

            />

        </div>

    );

};

export default WaiverContentEditor;